// Match the Rumio host's look (apps contract v1.1 §3).
//
// The host hands theme tokens over twice: a snapshot in the session JWT's
// `theme` claim, and live `rumio:design` postMessages (on handshake, when
// the admin edits the theme, and when the host toggles dark mode). Both
// carry the same ThemeTokens shape; we map it onto our semantic CSS
// variables (styles.css). Missing values keep the defaults.

import { getSessionToken } from './session';

export interface ThemeTokens {
  primary?: Record<string, string>;
  text?: { primary?: string; secondary?: string; muted?: string };
  bg?: { primary?: string; secondary?: string; tertiary?: string; warm?: string };
  fonts?: { sans?: string; display?: string };
  dark_mode?: boolean;
}

const MANAGED = [
  '--color-bg',
  '--color-surface',
  '--color-surface-2',
  '--color-border',
  '--color-text',
  '--color-muted',
  '--color-accent',
  '--color-accent-hover',
  '--color-accent-soft',
  '--font-sans',
] as const;

type Managed = (typeof MANAGED)[number];

/** Pure mapping from host tokens to our variables (only the ones present). */
export function themeVariables(tokens: ThemeTokens): Partial<Record<Managed, string>> {
  const dark = tokens.dark_mode === true;
  const p = tokens.primary ?? {};
  const vars: Partial<Record<Managed, string>> = {
    '--color-bg': tokens.bg?.primary,
    '--color-surface': tokens.bg?.secondary,
    '--color-surface-2': tokens.bg?.tertiary,
    '--color-border': dark ? p['800'] : p['200'],
    '--color-text': tokens.text?.primary,
    '--color-muted': tokens.text?.muted ?? tokens.text?.secondary,
    '--color-accent': dark ? p['400'] : p['600'],
    '--color-accent-hover': dark ? p['300'] : p['700'],
    '--color-accent-soft': dark ? p['900'] : p['50'],
    '--font-sans': tokens.fonts?.sans,
  };
  for (const key of Object.keys(vars) as Managed[]) {
    if (!vars[key]) delete vars[key];
  }
  return vars;
}

/** Apply host tokens, replacing whatever a previous call set. */
export function applyThemeTokens(tokens: ThemeTokens, root: HTMLElement = document.documentElement): void {
  for (const name of MANAGED) root.style.removeProperty(name);
  for (const [name, value] of Object.entries(themeVariables(tokens))) {
    root.style.setProperty(name, value);
  }
  root.classList.toggle('dark', tokens.dark_mode === true);
  root.style.colorScheme = tokens.dark_mode ? 'dark' : 'light';
}

/** The `theme` claim of the current session JWT, if any. */
export function themeFromSession(token: string | null = getSessionToken()): ThemeTokens | null {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { theme?: unknown };
    return claims.theme && typeof claims.theme === 'object' ? (claims.theme as ThemeTokens) : null;
  } catch {
    return null;
  }
}

/** Apply the JWT snapshot now and follow live `rumio:design` updates. */
export function startThemeSync(): () => void {
  const snapshot = themeFromSession();
  if (snapshot) applyThemeTokens(snapshot);
  if (typeof window === 'undefined' || window.parent === window) return () => {};

  const onMessage = (event: MessageEvent) => {
    if (event.source !== window.parent) return;
    const data = event.data as { type?: unknown; tokens?: unknown } | null;
    if (data?.type === 'rumio:design' && data.tokens && typeof data.tokens === 'object') {
      applyThemeTokens(data.tokens as ThemeTokens);
    }
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}
